import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, CheckCircle, Sparkles, Search, Send, Bot, AlertCircle, ChevronDown } from "lucide-react";
import { Badge } from "../ui/badge";

const conversation = {
  query: "Analyze my EOBs from the past month and let me know what I should change about my revenue cycle.",
  steps: [
    { text: "I'll start by gathering all the relevant data in parallel — EOB details, a...", status: "done" },
    { text: "Loaded EOB: Delta Dental of California — $0.00 paid, status: ready,...", status: "done" },
    { text: "EOB adjustments analyzed — 1 service line", status: "done" },
    { text: "Denial: hard severity", status: "alert" },
    { text: "Skill catalog loaded — 9 skills available", status: "done" },
    { text: "Good — I have the core EOB data.", status: "done" },
    { text: "1 clinical note loaded", status: "done" },
    { text: "Perio exam loaded — Stage III", status: "done" },
    { text: "Benefits loaded — ACTIVE coverage", status: "done" },
    { text: "Claim history: 5 claims loaded", status: "done" },
    { text: "No prior appeals", status: "search" },
    { text: "Adjudication history loaded — 98% denial rate", status: "done" },
    { text: "0 sibling claims on remittance", status: "search" },
    { text: "Patient balance: $0.00", status: "done" },
    { text: "Skills loaded: SRP Denial — Contradictory Perio Data Detection", status: "done" },
    { text: "The skill confirms: pockets ≥4mm + significant BOP → ★★APPEAL_C...", status: "done" },
    { text: "Now I have all the data I need.", status: "done" },
    { text: "Loaded challenge my reasoning", status: "challenge" },
    { text: "The reviewer has flagged important points.", status: "done" },
    { text: "Loaded challenge my reasoning", status: "challenge" },
    { text: "Reviewer approved.", status: "done" },
  ],
  recommendation: "APPEAL_CLAIM",
  confidence: "medium",
};

const analysis = {
  title: "EOB Analysis — Robert Johnson | Delta Dental of CA | D4341 SRP | $285",
  whatHappened: "Delta Dental of California issued a full denial (CO-50) on a D4341 periodontal scaling and root planing claim for $285, with two remark codes:",
  codes: [
    { code: "M127", desc: "Missing/incomplete clinical information" },
    { code: "N362", desc: "Replaced by alternate benefit" },
  ],
  insight: "The key insight here is that M127 is a documentation deficiency code, not a true non-covered exclusion. The payer is saying they didn't have enough clinical information to approve the claim — not that D4341 is excluded from the patient's plan.",
};

const PMSAshaCopilotSlide = () => {
  const [visibleSteps, setVisibleSteps] = useState(0);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [queryTyped, setQueryTyped] = useState(0);
  const [phase, setPhase] = useState("typing"); // typing, reasoning, analysis
  const stepsContainerRef = useRef(null);

  // Auto-scroll steps container when new steps appear
  useEffect(() => {
    if (stepsContainerRef.current) {
      stepsContainerRef.current.scrollTop = stepsContainerRef.current.scrollHeight;
    }
  }, [visibleSteps]);

  useEffect(() => {
    let cancelled = false;
    const cycle = async () => {
      while (!cancelled) {
        // Reset
        setPhase("typing");
        setVisibleSteps(0);
        setShowAnalysis(false);
        setQueryTyped(0);
        await new Promise((r) => setTimeout(r, 1200));

        // Type the query
        const queryText = conversation.query;
        for (let c = 0; c <= queryText.length; c += 2) {
          if (cancelled) return;
          setQueryTyped(Math.min(c, queryText.length));
          await new Promise((r) => setTimeout(r, 25));
        }
        setQueryTyped(queryText.length);
        await new Promise((r) => setTimeout(r, 600));

        // Reasoning steps
        setPhase("reasoning");
        for (let i = 1; i <= conversation.steps.length; i++) {
          if (cancelled) return;
          setVisibleSteps(i);
          await new Promise((r) => setTimeout(r, 350));
        }

        if (cancelled) return;
        await new Promise((r) => setTimeout(r, 800));

        // Show analysis
        setPhase("analysis");
        setShowAnalysis(true);

        await new Promise((r) => setTimeout(r, 8000));
      }
    };
    cycle();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[100px] opacity-60" />
      </div>

      <div className="flex flex-row h-full w-full max-w-[1600px] gap-10 relative z-10">
        {/* Left Side */}
        <div className="w-[38%] flex flex-col justify-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200 mb-4">
              <Bot className="w-4 h-4" />
              <span>PMS Feature</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              Asha{" "}
              <span className="text-blue-600">Copilot</span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Ask Asha anything — it retrieves data across your entire practice,
              performs intelligent multi-step reasoning, and delivers actionable
              answers with full context. Like having an expert analyst on demand.
            </p>
          </motion.div>

          <div className="flex flex-wrap gap-2">
            {[
              "Natural Language",
              "Multi-Step Reasoning",
              "Data Retrieval",
              "EOB Analysis",
              "Patient Lookup",
              "Scheduling Help",
            ].map((f, i) => (
              <motion.div
                key={f}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                {f}
              </motion.div>
            ))}
          </div>

          {/* Example queries */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
          >
            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wide mb-2">Ask anything</p>
            <div className="space-y-1.5">
              {[
                "How many patients are scheduled today?",
                "Which claims were denied this week?",
                "Show me unscheduled treatment over $1K",
              ].map((q, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                  <MessageSquare className="w-3 h-3 text-blue-400 flex-shrink-0" />
                  <span className="italic">"{q}"</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right Side: Copilot Chat Interface */}
        <div className="w-[62%] h-full flex items-center justify-center relative">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-[600px] relative z-10"
          >
            {/* Header */}
            <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                  <span className="text-white font-bold text-sm">A</span>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Asha Copilot</h3>
                  <p className="text-xs text-slate-500">asdf &bull; Location 3</p>
                </div>
              </div>
              <Badge className="bg-blue-600 text-white hover:bg-blue-700 border-0">
                <Sparkles className="w-3 h-3 mr-1" />
                AI Assistant
              </Badge>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* User Query Bubble */}
              <div className="flex justify-end">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="max-w-[80%] bg-blue-600 text-white rounded-2xl rounded-tr-md px-4 py-3"
                >
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {conversation.query.slice(0, queryTyped)}
                    {phase === "typing" && queryTyped < conversation.query.length && (
                      <motion.span
                        animate={{ opacity: [1, 0] }}
                        transition={{ duration: 0.5, repeat: Infinity }}
                        className="inline-block w-0.5 h-3.5 bg-white ml-0.5 align-middle"
                      />
                    )}
                  </p>
                </motion.div>
              </div>

              {/* Copilot Response */}
              {phase !== "typing" && (
                <div className="flex gap-3">
                  <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-[10px]">A</span>
                  </div>
                  <div className="flex-1 space-y-3">
                    {/* Reasoning Steps */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-blue-600" />
                          <span className="text-xs font-bold text-slate-700">
                            {visibleSteps} steps
                          </span>
                        </div>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                      </div>

                      <div ref={stepsContainerRef} className="max-h-[220px] overflow-y-auto px-3 py-2 space-y-0.5">
                        {conversation.steps.slice(0, visibleSteps).map((step, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2 }}
                            className="flex items-start gap-2 py-1"
                          >
                            {step.status === "done" ? (
                              <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                            ) : step.status === "alert" ? (
                              <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                            ) : step.status === "search" ? (
                              <Search className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                            ) : step.status === "challenge" ? (
                              <AlertCircle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0 mt-0.5" />
                            ) : (
                              <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                            )}
                            <span className="text-xs text-slate-600 leading-relaxed">{step.text}</span>
                          </motion.div>
                        ))}
                        {phase === "reasoning" && visibleSteps < conversation.steps.length && (
                          <motion.div
                            animate={{ opacity: [0.4, 1, 0.4] }}
                            transition={{ duration: 1, repeat: Infinity }}
                            className="flex items-center gap-2 py-1 px-1"
                          >
                            <div className="w-3.5 h-3.5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin flex-shrink-0" />
                            <span className="text-xs text-blue-600 font-medium">Analyzing...</span>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>

                    {/* Recommendation Badge */}
                    {showAnalysis && (
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-slate-700">
                          Recommendation: <span className="font-bold text-blue-700">{conversation.recommendation}</span>
                        </span>
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold border border-amber-200">
                          {conversation.confidence} confidence
                        </span>
                      </motion.div>
                    )}

                    {/* Analysis Card */}
                    {showAnalysis && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-white rounded-xl border border-slate-200 p-4 space-y-3"
                      >
                        <p className="text-xs text-slate-500">Here's my full analysis and recommendation for this EOB posting.</p>
                        <div className="border-t border-slate-100 pt-3">
                          <p className="text-sm font-bold text-slate-900 mb-2">{analysis.title}</p>
                          <p className="text-xs font-bold text-blue-600 mb-1">What Happened</p>
                          <p className="text-xs text-slate-600 leading-relaxed mb-2">{analysis.whatHappened}</p>
                          <div className="space-y-1 mb-3">
                            {analysis.codes.map((c, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs">
                                <span className="font-bold text-slate-900">{c.code}</span>
                                <span className="text-slate-600">— {c.desc}</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-slate-700 leading-relaxed">{analysis.insight}</p>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Input Bar */}
            <div className="h-14 border-t border-slate-100 px-5 flex items-center gap-3 bg-white">
              <input
                type="text"
                placeholder="How many patients are scheduled today?"
                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl py-2 px-4 text-sm text-slate-700 placeholder:text-slate-400"
                readOnly
              />
              <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-md">
                <Send className="w-4 h-4" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PMSAshaCopilotSlide;
